import React, { useCallback, useEffect, useState } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Plus, Download, Check, X, Rocket } from "lucide-react";
import { fmtDT } from "../components/security/ActivityTab";
import { ChangeCreateDialog, TYPE_LABEL } from "../components/change-log/ChangeCreateDialog";

const STATUS_STYLE = { pending_approval: "bg-amber-100 text-amber-700", approved: "bg-cyan-100 text-cyan-800", deployed: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700" };
const PAST = { approve: "approved", reject: "rejected", deploy: "marked deployed" };
const err = (e, m) => toast.error(e.response?.data?.detail || m);

function ChangeRow({ rec, me, onAction }) {
  const [note, setNote] = useState("");
  const mine = rec.author.id === me;
  const canApprove = !mine && !rec.approver && ["pending_approval", "deployed"].includes(rec.status);
  const canDeploy = rec.status === "approved" || (rec.status === "pending_approval" && rec.change_type === "emergency");
  return (
    <tr className="border-b last:border-0 align-top" data-testid={`change-row-${rec.ref}`}>
      <td className="p-3 font-mono text-xs">{rec.ref}</td>
      <td className="p-3"><p className="font-medium">{rec.title}</p><p className="text-xs text-slate-500">{TYPE_LABEL[rec.change_type]} · {rec.risk} risk</p>{rec.description && <p className="text-xs text-slate-600 mt-1 max-w-md">{rec.description}</p>}</td>
      <td className="p-3 text-xs"><p>{rec.author.name}</p><p className="text-slate-400">{fmtDT(rec.created_at)}</p></td>
      <td className="p-3 text-xs">{rec.approver ? <><p>{rec.approver.name}</p><p className="text-slate-400">{fmtDT(rec.approved_at)}</p></> : <span className="text-slate-400">—</span>}</td>
      <td className="p-3">
        <Badge className={`text-xs rounded-sm ${STATUS_STYLE[rec.status]}`}>{rec.status.replace("_", " ")}</Badge>
        {rec.retro_approval_required && <p className="text-[11px] text-red-600 mt-1" data-testid={`retro-flag-${rec.ref}`}>Released before approval</p>}
      </td>
      <td className="p-3">
        <div className="flex flex-col gap-1.5 items-end">
          {canApprove && <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (needed to reject)" className="h-8 text-xs w-48" data-testid={`change-note-${rec.ref}`} />}
          <div className="flex gap-1.5">
            {canApprove && <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => onAction(rec, "approve", note)} data-testid={`change-approve-${rec.ref}`}><Check size={13} /> Approve</Button>}
            {canApprove && rec.status === "pending_approval" && <Button size="sm" variant="outline" className="h-8 gap-1 text-red-700" onClick={() => onAction(rec, "reject", note)} data-testid={`change-reject-${rec.ref}`}><X size={13} /> Reject</Button>}
            {canDeploy && <Button size="sm" className="h-8 gap-1" onClick={() => onAction(rec, "deploy")} data-testid={`change-deploy-${rec.ref}`}><Rocket size={13} /> Mark deployed</Button>}
          </div>
          {mine && rec.status === "pending_approval" && <p className="text-[11px] text-slate-400">Waiting for a second person</p>}
        </div>
      </td>
    </tr>
  );
}

export default function ChangeLogPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [denied, setDenied] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => apiClient.get("/change-records").then((r) => setRows(r.data))
    .catch((e) => (e.response?.status === 403 ? setDenied(true) : err(e, "Could not load changes"))), []);
  useEffect(() => { load(); }, [load]);

  const create = async (body) => {
    try { const r = await apiClient.post("/change-records", body); toast.success(`${r.data.ref} submitted for approval`); setCreating(false); await load(); return true; }
    catch (e) { err(e, "Could not record change"); return false; }
  };
  const act = async (rec, action, note) => {
    try { await apiClient.post(`/change-records/${rec.id}/${action}`, action === "deploy" ? {} : { note }); toast.success(`${rec.ref} ${PAST[action]}`); await load(); }
    catch (e) { err(e, "Action failed"); }
  };

  if (denied) return <Card data-testid="change-log-denied"><CardContent className="py-16 text-center text-sm text-slate-500">Only ConTrack platform staff can manage change records.</CardContent></Card>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="change-log-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>Change Log</h1>
          <p className="text-slate-500 mt-1">Sign-off record for every change to ConTrack, in line with the Change Management Policy (POL-06)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm gap-2" onClick={() => window.open(`${apiClient.defaults.baseURL}/change-records/export/csv`, "_blank")} data-testid="export-changes-btn"><Download size={15} /> CSV</Button>
          <Button className="rounded-sm gap-2" onClick={() => setCreating(true)} data-testid="record-change-btn"><Plus size={15} /> Record change</Button>
        </div>
      </div>
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {rows && !rows.length ? <p className="p-10 text-center text-sm text-slate-500" data-testid="changes-empty">No changes recorded yet.</p> : (
            <table className="w-full text-sm" data-testid="changes-table">
              <thead><tr className="border-b text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
                <th className="p-3">Ref</th><th className="p-3">Change</th><th className="p-3">Author</th><th className="p-3">Approved by</th><th className="p-3">Status</th><th className="p-3" />
              </tr></thead>
              <tbody>{(rows || []).map((r) => <ChangeRow key={r.id} rec={r} me={user?.id} onAction={act} />)}</tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <ChangeCreateDialog open={creating} onOpenChange={setCreating} onCreate={create} />
    </div>
  );
}
